import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { selectUser } from 'selectors';
import styled from 'styled-components';
import { Container, responsive } from 'styled-minimal';

import { GoogleApiContext } from 'modules/GoogleApiContext';
import { useAppSelector } from 'modules/hooks';
import { appColor, headerHeight, spacer } from 'modules/theme';

import { grades } from 'config';

import { logOut } from 'actions';

import Icon from 'components/Icon';
import Logo from 'components/Logo';

import Modal from './Modal';

const HeaderWrapper = styled.header`
  background-color: #374151;
  color: white;
  height: ${headerHeight}px;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 200;

  &:before {
    background-color: ${appColor};
    bottom: 0;
    content: '';
    height: 0.2rem;
    left: 0;
    position: absolute;
    right: 0;
  }
`;

const HeaderContainer = styled(Container)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  height: 100%;
  justify-content: space-between;
  padding-bottom: ${spacer(2)};
  padding-top: ${spacer(2)};
`;

const Actions = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const Action = styled.button`
  align-items: center;
  color: #fff;
  display: flex;
  font-size: 1.3rem;
  padding: ${spacer(2)};

  ${responsive({ md: { fontSize: '1.6rem' } })}; /* stylelint-disable-line */

  &.active {
    color: #fff;
  }
  span {
    display: inline-block;
    margin-right: 0.4rem;
    text-transform: none;
  }

  .icon {
    border-radius: 50px;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    margin-right: 8px;
    border: 3px solid transparent;
    color: #374151;
  }

  &:hover .icon {
    background-color: lightgrey;
  }

  &:active .icon {
    color: white;
    background-color: #555;
  }
`;

const Title = styled.span`
  font-size: 1.6rem;
  font-weight: 700;
  display: none;
  ${responsive({ md: { display: 'block' } })};
  // position: absolute;
  // left: calc(50% - 144px);
`;

export default function Header() {
  const user = useAppSelector(selectUser);
  const { coach, grade, messages, questions } = user;
  const question = questions[grade][coach];
  const dispatch = useDispatch();
  const { loaded, tokenClient } = useContext(GoogleApiContext);

  const handleClickLogout = () => {
    dispatch(logOut());
  };

  const withGoogleApi = (callback: () => void) => {
    if (tokenClient == null) {
      return;
    }

    (tokenClient as any).callback = async (resp: any) => {
      if (resp.error !== undefined) {
        throw resp;
      }

      callback();
    };

    if ((window as any).gapi.client.getToken() === null) {
      // Prompt the user to select a Google Account and ask for consent to share their data
      // when establishing a new session.
      (tokenClient as any).requestAccessToken({ prompt: 'consent' });
    } else {
      // Skip display of account chooser and consent dialog for an existing session.
      (tokenClient as any).requestAccessToken({ prompt: '' });
    }
  };

  const handleSave = () => {
    withGoogleApi(() => {
      setShowModal(true);
    });
  };

  const [showModal, setShowModal] = useState(false);
  const [filename, setFilename] = useState('');

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilename(event.target.value);
  };

  const handleSubmit = async () => {
    const createResponse = await (window as any).gapi.client.docs.documents.create({
      title: filename,
    });

    console.log(createResponse.result);

    // now that we created the doc, let's add content using the
    // documentId returned from the create call.
    const documentId = createResponse?.result?.documentId; // nullish coalescing operator

    if (documentId) {
      let text = '';
      // const ranges: number[][] = [];

      messages[grade][coach].forEach((value, index) => {
        if (index < 2) {
          return;
        }

        const content = value.content.replaceAll('\r', '\r\n');

        if (value.role === 'assistant') {
          // ranges.push([text.length + 1, text.length + 8]);
          text += `ChatGPT4\r\n${content}\r\n\r\n`;
        } else if (value.role === 'user') {
          // ranges.push([text.length + 1, text.length + 12 + value.content.length]);
          text += `User Input: ${content}\r\n\r\n`;
        }
      });
      text += `ChatGPT4\r\n${question}`;
      const updateResponse = await (window as any).gapi.client.docs.documents.batchUpdate({
        documentId,
        requests: [
          {
            insertText: {
              endOfSegmentLocation: {},
              text,
            },
          },
        ],
      });

      console.log(updateResponse.result);
      window.open(`https://docs.google.com/document/d/${documentId}/edit`);
    }

    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
  };

  return (
    <HeaderWrapper data-testid="Header">
      <HeaderContainer>
        <Actions>
          <Logo />
          <Title>Coachbot {grades[grade].name}</Title>
        </Actions>
        <Actions>
          {loaded && question && (
            <Action onClick={handleSave}>
              <div className="icon">
                <Icon name="save" width={16} />
              </div>
              <span>Save</span>
            </Action>
          )}
          <Action onClick={handleClickLogout}>
            <div className="icon">
              <Icon name="start-over" width={32} />
            </div>
            <span>Start Over</span>
          </Action>
        </Actions>
        {/* <Modal isOpen={open} onClose={() => setOpen(false)} /> */}
        {showModal && (
          <Modal
            filename={filename}
            onCancel={handleCancel}
            onFilenameChange={handleFilenameChange}
            onSubmit={handleSubmit}
            title="Save to Google Docs"
          />
        )}
      </HeaderContainer>
    </HeaderWrapper>
  );
}
